/*
 * ===============================================================================================================
 *                                Copyright 2021, Blue Yonder Group, Inc.
 *                                           All Rights Reserved
 *
 *                               THIS IS UNPUBLISHED PROPRIETARY SOURCE CODE OF
 *                                          BLUE YONDER GROUP, INC.
 *
 *
 *                         The copyright notice above does not evidence any actual
 *                                 or intended publication of such source code.
 *
 * ===============================================================================================================
 */
import _ from 'lodash';
import { useHistory } from 'react-router-dom';
import { Box, Button, Grid } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { useCallback, useEffect, useState } from 'react';
import { LuiEmptyState, LuiSearch, LuiToolbar } from '@jda/lui-common-component-library-mui5';
import {
  MRT_ToggleFullScreenButton as ToggleFullScreenButton,
  MRT_ShowHideColumnsButton as ShowHideColumnsButton,
  MRT_ToggleDensePaddingButton as ToggleDensePaddingButton
} from 'material-react-table';

import { useStyles } from './styles';
import SnackbarAlert from '../../shared/SnackbarAlert';
import { UserGroupsList } from '../../components/UserGroups';
import { getAllUserGroupsRequest } from '../../services/userGroups';
import { selectResourcePermissions } from '../../store/auth/authSelector';
import { actions, resources } from '../../shared/CheckPermissions/constants';
import { selectSnackBarSuccess } from '../../store/snackBar/snackBarSelector';
// import { selectAllUserGroups } from '../../store/userGroups/userGroupSelector';
import { setSnackBarMessage } from '../../store/snackBar/snackBarActionCreator';
import { checkResourcePermission } from '../../shared/CheckPermissions/CheckPermission';
import { getAllUserGroupsAction, setUserGroupSearchValueAction } from '../../store/userGroups/userGroupsActionCreator';
import {
  ADD_USER_GROUP,
  EMPTY_SCREEN_TITLE,
  ERROR_FETCHING_USER_GROUPS,
  NO_USER_GROUP_MESSAGE,
  SEARCH_USER_GROUP,
  USER_GROUPS
} from './constants';
import { UserGroup as Group} from '@jda/lui-common-icon-library-mui5';

const UserGroups = () => {
  const { classes } = useStyles();
  const history = useHistory();
  const dispatch = useDispatch();

  // const userGroups = useSelector(selectAllUserGroups);
  const selectSuccessMessage = useSelector(selectSnackBarSuccess);
  const resourcePermissions = useSelector(selectResourcePermissions);

  // const [showLoader, setShowLoader] = useState(false);
  const [searchValue, setSearchValue] = useState<string>('');

  const createPermission =
    resourcePermissions &&
    resourcePermissions.resources &&
    checkResourcePermission(actions.CREATE, resourcePermissions.resources[resources.GROUPS]);
  const viewPermission =
    resourcePermissions &&
    resourcePermissions.resources &&
    checkResourcePermission(actions.VIEW, resourcePermissions.resources[resources.GROUPS]);

  useEffect(() => {
    getAllUserGroupData('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getAllUserGroupData = async (searchString: string) => {
    // setShowLoader(true);
    const queryParams = {
      pageSize: 10,
      pageNumber: 1,
      searchValue: searchString
    };
    try {
      const res: any = await getAllUserGroupsRequest(queryParams);
      if (res && res.data) {
        dispatch(getAllUserGroupsAction(res));
        // setShowLoader(false);
      }
    } catch (error: any) {
      // setShowLoader(false);
      const errorMessage: any = {
        message: ERROR_FETCHING_USER_GROUPS + error.message,
        severity: 'error',
        showSnackbar: true
      };
      dispatch(setSnackBarMessage(errorMessage));
    }
  };
  const debounceFn = useCallback(_.debounce(getAllUserGroupData, 500), [searchValue]);

  const onSearchTextBoxChange = (e: any) => {
    const searchString = e.target.value;
    setSearchValue(searchString);
    if (!searchString.length || !searchString.trim().length) {
      getAllUserGroupData('');
    } else if (searchString.length > 2) {
      debounceFn(searchString);
    }
    dispatch(setUserGroupSearchValueAction(searchString));
  };

  const handleOnClear = () => {
    setSearchValue('');
    getAllUserGroupData('');
    dispatch(setUserGroupSearchValueAction(''));
  };

  const onEdit = (data: any) => {
    if (history.location.search) {
      history.push(`/userGroup/${data.identifier}${history.location.search}`);
    } else {
      history.push(`/userGroup/${data.identifier}`);
    }
  };

  const createUserGroup = () => {
    if (history.location.search) {
      history.push(`userGroup${history.location.search}`);
    } else {
      history.push('userGroup');
    }
  };
  const extendedToolbar = ({ table }) => {
    const leftComponents = {
      component: (
        <Box className={classes.searchBarWrap}>
          <LuiSearch
            size='small'
            value={searchValue}
            onChange={onSearchTextBoxChange}
            placeholder={SEARCH_USER_GROUP}
            onClear={handleOnClear}
          />
        </Box>
      )
    };
    const rightComponents = {
      component: (
        <Box>
          {viewPermission && (
            <Button
              size="small"
              variant="contained"
              onClick={createUserGroup}
              className={classes.addButton}
              startIcon={<Group />}
              data-testid="add-user-group-button"
            >
              {ADD_USER_GROUP}
            </Button>
          )}
          <ShowHideColumnsButton table={table} />
          <ToggleDensePaddingButton table={table} />
          <ToggleFullScreenButton table={table} />
        </Box>
      )
    };
    return <LuiToolbar className={classes.toolbarPanel} leftZone={leftComponents} rightZone={rightComponents} />;
  };
  const emptyRowsFallback = () => {
    return (
      <Box my={5}>
        <LuiEmptyState
          imageItem={null}
          heading={ADD_USER_GROUP}
          subHeading={`${createPermission ? EMPTY_SCREEN_TITLE : NO_USER_GROUP_MESSAGE}`}
        />
      </Box>
    );
  };
  return (
    <>
      <Box>
        <Grid className={classes.root}>
          <Grid container className={classes.mappingList}>
            <Grid item xs={12} sm={12}>
              <UserGroupsList onEdit={onEdit} refreshOnDelete={getAllUserGroupData} extendedToolbar={extendedToolbar} emptyRowsFallback={emptyRowsFallback} />
            </Grid>
          </Grid>
        </Grid>
        {selectSuccessMessage && selectSuccessMessage.showSnackbar && (
          <Grid container>
            <SnackbarAlert />
          </Grid>
        )}
      </Box>
    </>
  );
};

const UserGroup = {
  component: UserGroups,
  name: USER_GROUPS
};

export default UserGroup;
